import React, { useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { TableRow, TableCell } from "@/components/ui/table";
import { TrashIcon } from "lucide-react";
import { InventoryItemForm } from "./InventoryTable";
import QuantityInput from "./QuantityInput";

// Define row-specific props
type InventoryTableRowProps = {
  item: InventoryItemForm;
  selectedCategory: string | null;
  updateCustomItem: (
    uuid: string,
    field: keyof InventoryItemForm,
    value: string | number
  ) => void;
  onQuantityChange: (item: InventoryItemForm, quantity: number) => void;
  onDeleteItem?: (uuid: string) => void;
  getItemQuantity: (uuid: string) => number;
  customerFacing?: boolean;
};

const humanizeCategory = (category: string | null): string => {
  if (!category) return "Uncategorized";

  // Replace underscores with spaces and capitalize each word
  return category
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const InventoryTableRow = ({
  item,
  selectedCategory,
  updateCustomItem,
  onQuantityChange,
  onDeleteItem,
  getItemQuantity,
  customerFacing = false,
}: InventoryTableRowProps) => {
  console.debug(`Rendering row for: ${item.name} (${item.uuid})`);

  const handleQuantityChange = useCallback(
    (itemParam: InventoryItemForm, quantity: number) => {
      onQuantityChange(itemParam, quantity);
    },
    [onQuantityChange]
  );

  return (
    <TableRow>
      {!customerFacing && !selectedCategory && (
        <TableCell>{humanizeCategory(item.room_or_type)}</TableCell>
      )}
      <TableCell>
        {item.mover_inventory_item_id ? (
          item.name
        ) : (
          <Input
            defaultValue={item.name}
            onChange={(e) => {
              updateCustomItem(item.uuid, "name", e.target.value);
            }}
            placeholder="Item name"
          />
        )}
      </TableCell>
      {!customerFacing && (
        <TableCell>
          {item.mover_inventory_item_id ? (
            item.volume_cuft
          ) : (
            <Input
              type="number"
              value={item.volume_cuft}
              onChange={(e) =>
                updateCustomItem(
                  item.uuid,
                  "volume_cuft",
                  parseFloat(e.target.value)
                )
              }
            />
          )}
        </TableCell>
      )}
      {!customerFacing && (
        <TableCell>
          {item.mover_inventory_item_id ? (
            item.weight_lbs
          ) : (
            <Input
              type="number"
              value={item.weight_lbs || 0}
              onChange={(e) =>
                updateCustomItem(
                  item.uuid,
                  "weight_lbs",
                  parseFloat(e.target.value)
                )
              }
            />
          )}
        </TableCell>
      )}
      <TableCell>
        <QuantityInput
          item={item as InventoryItemForm}
          onChange={handleQuantityChange}
          initialQuantity={getItemQuantity(item.uuid)}
        />
      </TableCell>
      {/* <TableCell>
        {item.total_volume_cuft?.toFixed(2)} /{" "}
        {item.total_weight_lbs?.toFixed(2)}
      </TableCell> */}
      {!customerFacing && onDeleteItem && (
        <TableCell>
          {!item.mover_inventory_item_id && !item.id && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDeleteItem(item.uuid)}
            >
              <TrashIcon className="w-4 h-4 text-red-500" />
            </Button>
          )}
        </TableCell>
      )}
    </TableRow>
  );
};

export default React.memo(InventoryTableRow, (prevProps, nextProps) => {
  return (
    prevProps.item.uuid === nextProps.item.uuid &&
    prevProps.item.name === nextProps.item.name &&
    prevProps.item.volume_cuft === nextProps.item.volume_cuft &&
    prevProps.item.weight_lbs === nextProps.item.weight_lbs &&
    prevProps.item.quantity === nextProps.item.quantity &&
    prevProps.item.total_volume_cuft === nextProps.item.total_volume_cuft &&
    prevProps.item.total_weight_lbs === nextProps.item.total_weight_lbs &&
    prevProps.selectedCategory === nextProps.selectedCategory &&
    prevProps.getItemQuantity(prevProps.item.uuid) ===
      nextProps.getItemQuantity(nextProps.item.uuid)
  );
});
